import { InjectionToken } from '@angular/core';
import { LocaleService } from '@next/core-lib/i18n';
import { SeriesOptionsType } from 'highcharts';
import { formatLocalizedUtcTime } from 'src/app/shared/utils/chart-date-format.utils';

import { DiseaseChartDay, DiseaseChartDetailsWeather, WeatherData } from './models';

export interface IconMapType {
  [key: string]: { iconPath: string; color: string };
}

export const CHART_CONFIG = new InjectionToken<BaseChartConfig>('CHART_CONFIG');

export abstract class BaseChartConfig {
  abstract getRightAxisTitle(localeService?: LocaleService): string;
  abstract getInfectionRiskLabel(localeService: LocaleService): string;
  abstract getIsHourlyData(): boolean;

  // --- Overridable methods with defaults ---

  getIsInfectionRiskHourly(): boolean {
    return false;
  }

  calculateRisk(day: DiseaseChartDay): number | undefined {
    if (day.infectionRiskPercentage != null) {
      return day.infectionRiskPercentage === 0 ? 0 : day.infectionRiskPercentage;
    }
    return day.infectionRisk;
  }

  extractWeatherData(days: DiseaseChartDay[]): WeatherData {
    const data: WeatherData = {
      precipitation: [],
      humidity: [],
      temperature: [],
      irrigation: [],
      leafWetness: [],
    };
    for (const day of days) {
      collectHourlyWeatherData(data, day, new Date(day.date));
    }
    return data;
  }

  buildAdditionalSeries(_localeService: LocaleService, _days: DiseaseChartDay[]): SeriesOptionsType[] {
    return [];
  }

  getShowAllLabel(localeService: LocaleService): string {
    return localeService.translate('shared.insightChart.showAll');
  }

  getPrecipitationLabel(localeService: LocaleService): string {
    return localeService.translate('shared.insightChart.precipitation');
  }

  getTemperatureLabel(localeService: LocaleService): string {
    return localeService.translate('shared.insightChart.temperature');
  }

  getHumidityLabel(localeService: LocaleService): string {
    return localeService.translate('shared.insightChart.humidity');
  }

  getIrrigationLabel(localeService: LocaleService): string {
    return localeService.translate('shared.insightChart.irrigation');
  }

  getLegendIconMap(): IconMapType {
    return {
      infectionRisk: { iconPath: 'infection-risk.svg', color: '#B5061A' },
      precipitation: { iconPath: 'rain_storm.svg', color: '#A1BAD7' },
      irrigation: { iconPath: 'irrigation.svg', color: '#2979FF' },
      temperature: { iconPath: 'temperature.svg', color: '#E4B005' },
      humidity: { iconPath: 'humidity.svg', color: '#8a8a86' },
    };
  }

  getHiddenLegendIds(): string[] {
    return ['emergenceDate'];
  }

  // --- private utility methods ---

  protected extractInfectionRisks(days: DiseaseChartDay[]): Array<[number, number]> {
    const infectionRisks: Array<[number, number]> = [];
    for (const day of days) {
      const risk = this.calculateRisk(day);
      if (risk != null) {
        const dateTime = toUTCDateMs(new Date(day.date), 12);
        infectionRisks.push([dateTime, risk]);
      }
    }
    return infectionRisks;
  }

  // --- Concrete methods ---

  getChartSeriesOptions(
    plotBands: Highcharts.XAxisPlotBandsOptions[],
    xMin: number,
    xMax: number,
    locale?: string,
    localeService?: LocaleService,
  ): Highcharts.Options {
    const iconMap = this.getLegendIconMap();
    const hiddenLegendIds = this.getHiddenLegendIds();
    const rightAxisTitle = this.getRightAxisTitle(localeService);

    return {
      lang: locale ? { locale } : undefined,
      time: {
        timezone: 'UTC',
      },
      chart: {
        height: '400px',
        marginTop: 72,
        ignoreHiddenSeries: false,
      },
      title: {
        text: '',
      },
      credits: {
        enabled: false,
      },
      plotOptions: {
        area: {
          states: {
            hover: {
              enabled: true,
              lineWidth: 0,
              marker: {
                enabled: false,
              },
              fillColor: {
                linearGradient: {
                  x1: 0,
                  y1: 0,
                  x2: 0,
                  y2: 1,
                },
                stops: [],
              },
            },
          },
        },
        series: {
          states: {
            hover: {
              enabled: true,
            },
          },
          stickyTracking: false,
          marker: {
            enabled: false,
          },
        },
      },
      xAxis: {
        type: 'datetime',
        dateTimeLabelFormats: {
          hour: '%H:%M',
          day: '%e',
        },
        tickInterval: 3600 * 1000,
        plotBands,
        min: xMin,
        max: xMax,
        labels: {
          style: {
            fontSize: '10px',
            color: '#999',
          },
          formatter() {
            const tickValue = Number(this.value);
            if (!Number.isFinite(tickValue)) {
              return '';
            }

            const tickDate = new Date(tickValue);
            if (tickDate.getUTCHours() === 0) {
              const dayLabel = formatDate(tickDate, locale || 'en');
              return `<span style="font-weight:bold;font-size:12px;color:#909090">${dayLabel}</span>`;
            }

            return `<span style="font-size:10px;color:#999">${formatLocalizedUtcTime(tickDate, locale || 'en')}</span>`;
          },
          useHTML: true,
        },
      },
      yAxis: [
        // Y-axis left side config
        {
          min: 0,
          max: 100,
          endOnTick: true,
          title: {
            text: '% / °C',
          },
        },
        //  Y-axis right side config
        {
          endOnTick: true,
          opposite: true,
          title: {
            text: rightAxisTitle,
          },
        },
      ],
      tooltip: {
        shared: false,
        useHTML: true,
        outside: true,
        backgroundColor: 'rgba(255, 255, 255, 1)',
        style: { color: '#333' },
        borderWidth: 1,
        borderColor: '#ddd',
        borderRadius: 4,
        shadow: true,
        zIndex: 50,
        padding: 8,
        className: 'highcharts-tooltip-above-all',

        formatter() {
          let symbol;
          const series = this.series as {
            symbol?: string;
            options?: {
              custom?: { isHourly?: boolean };
              tooltip?: { valueSuffix?: string };
            };
          };
          const seriesOptions = (series.options ?? {}) as {
            custom?: { isHourly?: boolean };
            tooltip?: { valueSuffix?: string };
          };

          switch (series.symbol) {
            case 'circle':
              symbol = '●';
              break;
            case 'diamond':
              symbol = '♦';
              break;
            case 'square':
              symbol = '■';
              break;
            case 'triangle':
              symbol = '▲';
              break;
            case 'triangle-down':
              symbol = '▼';
              break;
            default:
              symbol = '●';
              break;
          }

          let tooltipContent: string;
          const date = new Date(this.x);
          const isHourly = seriesOptions.custom?.isHourly === true;

          if (isHourly) {
            tooltipContent =
              formatDate(date, locale || 'en') +
              ', ' +
              date.toLocaleTimeString(locale || 'en', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
                timeZone: 'UTC',
              });
          } else {
            tooltipContent = formatDate(date, locale || 'en');
          }

          return (
            tooltipContent +
            `<br> <span style="color:${typeof this.color === 'string' ? this.color : ''}; z-index:99;">${symbol}</span>
            ${this.series.name}: <b> ${this.y} ${seriesOptions.tooltip?.valueSuffix} </b>`
          );
        },
      },
      legend: {
        useHTML: true,
        itemDistance: 8,
        itemMarginTop: 0,
        itemMarginBottom: 8,
        layout: 'horizontal',
        align: 'left',
        verticalAlign: 'top',
        y: 4,
        x: 8,
        padding: 0,
        symbolWidth: 0,
        symbolHeight: 0,

        labelFormatter() {
          // Show All button
          if (this.options.id === 'showAll') {
            return `
              <div class="chart-legend-item show-all-button" style="
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 4px;
                box-sizing: border-box;
                height: 32px;
                padding: 4px 8px;
                border: 1px solid #ddd;
                border-radius: 6px;
                background-color: #fff;
                font-size: 12px;
                color: #333;
                cursor: pointer;
                max-width: 100%;
              ">
                <span style="
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  min-width: 20px;
                  min-height: 20px;
                  border-radius: 50%;
                  color: #fff;
                ">
                  <img src="../../../../../../../../assets/icons/eye.svg" alt="Show All" style="width: 16px; height: 16px;" />
                </span>
                <span class="legend-text" style="flex-grow: 1; text-align: left; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                  ${this.name}
                </span>
              </div>
            `;
          }

          // Hide series in the hiddenLegendIds list
          if (hiddenLegendIds.includes(String(this.options.id))) {
            return ``;
          }

          const seriesId = String(this.options.id);
          const seriesInfo = iconMap[seriesId] || { iconPath: 'assets/icons/default.svg', color: '#000' };

          return `
            <div class="chart-legend-item" style="
              display: flex;
              align-items: center;
              justify-content: flex-start;
              gap: 4px;
              box-sizing: border-box;
              height: 32px;
              padding: 4px 8px;
              border: 1px solid #ddd;
              border-radius: 6px;
              background-color: #fff;
              font-size: 12px;
              color: #333;
              cursor: pointer;
              max-width: 100%;
              ${this.visible ? '' : 'opacity: 0.5;'}
            ">
              <span style="
                display: flex;
                align-items: center;
                justify-content: center;
                min-width: 20px;
                min-height: 20px;
                border-radius: 50%;
                background-color: ${seriesInfo.color};
              ">
                <img src="../../../../../../../assets/icons/${seriesInfo.iconPath}" alt="${this.name}" style="width: 16px; height: 16px;" />
              </span>
              <span class="legend-text" style="
                flex-grow: 1;
                text-align: left;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              ">
                ${this.name}
              </span>
              <span style="min-width: 16px; display: flex; justify-content: center;">
                <img
                  src="../../../../../../../assets/icons/${this.visible ? 'eye.svg' : 'eye-lock.svg'}"
                  alt="${this.visible ? 'Visible' : 'Hidden'}"
                  style="width: 16px; height: 16px;" />
              </span>
            </div>
          `;
        },
      },
    } as Highcharts.Options;
  }

  getSeriesOptions(
    localeService: LocaleService,
    days: DiseaseChartDay[],
    emergenceDateMs?: number,
  ): SeriesOptionsType[] {
    const isHourlyData = this.getIsHourlyData();
    const columnPointRange = isHourlyData ? 3600 * 1000 : 24 * 3600 * 1000;
    const series: SeriesOptionsType[] = [];

    const infectionRisks = this.extractInfectionRisks(days);
    const weatherData = this.extractWeatherData(days);

    const showAllButtonDummySeries: SeriesOptionsType = {
      type: 'line',
      id: 'showAll',
      name: this.getShowAllLabel(localeService),
      data: [],
      showInLegend: true,
      visible: true,
      events: {
        legendItemClick(e) {
          e.preventDefault();
          const chart = this.chart;
          const allSeries = chart.series;
          allSeries.forEach((s) => {
            if (!s.visible && s.options.id !== 'showAll') {
              s.setVisible(true, false);
            }
          });
          chart.redraw();
          return false;
        },
      },
    };

    const infectionRiskSeriesOptions: SeriesOptionsType = {
      id: 'infectionRisk',
      type: 'spline',
      name: this.getInfectionRiskLabel(localeService),
      data: infectionRisks,
      tooltip: {
        valueSuffix: ' %',
      },
      color: '#B5061A',
      custom: { isHourly: this.getIsInfectionRiskHourly() },
    };

    const precipitationSeriesOptions: SeriesOptionsType = {
      id: 'precipitation',
      type: 'column',
      name: this.getPrecipitationLabel(localeService),
      data: weatherData.precipitation,
      tooltip: {
        valueSuffix: ' mm',
      },
      color: '#A1BAD7',
      yAxis: 1,
      pointRange: columnPointRange,
      pointPlacement: 'on',
      pointPadding: 0,
      groupPadding: 0,
      custom: { isHourly: isHourlyData },
    };

    const temperatureSeriesOptions: SeriesOptionsType = {
      id: 'temperature',
      type: 'spline',
      name: this.getTemperatureLabel(localeService),
      data: weatherData.temperature,
      tooltip: {
        valueSuffix: ' °C',
      },
      color: '#E4B005',
      custom: { isHourly: isHourlyData },
    };
    const humiditySeriesOptions: SeriesOptionsType = {
      id: 'humidity',
      type: 'spline',
      name: this.getHumidityLabel(localeService),
      data: weatherData.humidity,
      tooltip: {
        valueSuffix: ' %',
      },
      color: '#8a8a86',
      custom: { isHourly: isHourlyData },
    };

    series.push(
      showAllButtonDummySeries,
      precipitationSeriesOptions,
      temperatureSeriesOptions,
      humiditySeriesOptions,
      infectionRiskSeriesOptions,
    );

    if (weatherData.irrigation && weatherData.irrigation.length > 0) {
      const irrigationSeriesOptions: SeriesOptionsType = {
        id: 'irrigation',
        type: 'column',
        name: this.getIrrigationLabel(localeService),
        data: weatherData.irrigation,
        tooltip: {
          valueSuffix: ' mm',
        },
        color: '#2979FF',
        yAxis: 1,
        pointRange: columnPointRange,
        pointPlacement: 'on',
        pointPadding: 0,
        groupPadding: 0,
        custom: { isHourly: isHourlyData },
      };
      series.push(irrigationSeriesOptions);
    }

    // Module-specific series
    const additionalSeries = this.buildAdditionalSeries(localeService, days);
    series.push(...additionalSeries);

    // Add emergence date if dates are provided
    if (emergenceDateMs) {
      const emergenceDateBar: (number | null)[][] = [
        [emergenceDateMs, 0],
        [emergenceDateMs, 100],
        [emergenceDateMs, null],
      ];

      const sprayTimeSeriesOptions: SeriesOptionsType = {
        id: 'emergenceDate',
        type: 'spline',
        name: 'emergenceDate',
        data: emergenceDateBar,
        tooltip: {
          pointFormat: '',
        },
        color: '#000',
        lineWidth: 4,
        enableMouseTracking: false,
      };

      series.push(sprayTimeSeriesOptions);
    }

    return series;
  }
}

// --- Shared utility functions ---

export function formatDate(date: Date, locale: string, options?: Intl.DateTimeFormatOptions): string {
  return date.toLocaleDateString(locale, { day: 'numeric', month: 'short', timeZone: 'UTC', ...options });
}

export function toUTCDateMs(date: Date, hour = 0): number {
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), hour);
}

export function toWeatherMap(weather: DiseaseChartDetailsWeather[]): Map<number, DiseaseChartDetailsWeather> {
  return new Map(weather.map((w) => [w.hour, w]));
}

export function collectHourlyWeatherData(data: WeatherData, day: DiseaseChartDay, date: Date): void {
  if (!day.weather?.length) return;

  const weatherByHour = toWeatherMap(day.weather);
  for (let i = 0; i < day.weather.length; i++) {
    const weather = weatherByHour.get(i);
    const dateTime = toUTCDateMs(date, i);
    const weatherPrecipitation =
      weather?.precipitation === undefined ? undefined : Number.parseFloat(weather.precipitation.toFixed(1));

    const irrigation = weather?.irrigation;

    data.precipitation.push([dateTime, weatherPrecipitation]);
    data.humidity.push([dateTime, weather?.humidity]);
    data.temperature.push([dateTime, weather?.temperature]);
    if (irrigation !== undefined) {
      data.irrigation.push([dateTime, irrigation]);
    }
  }
}
